<template>
  <b-row>
    <b-col sm="3">
      <strong>Details</strong>
      <p>Enter the general details of this opportunity here.</p>
    </b-col>
    <b-col sm="9">
      <b-form class="form-horizontal">
        <b-form-group
          label="Name / Title"
          label-for="opportunityName"
          label-class="control-label opportunity-form-label"
        >
          <b-form-input v-model="name" type="text"></b-form-input>
        </b-form-group>

        <b-form-group
          label="Type"
          label-for="opportunityType"
          label-class="control-label opportunity-form-label required"
          :class="{ 'invalid-group': $v.opportunityTypeId.$error }"
        >
          <b-form-select
            v-model="opportunityTypeId"
            :options="types"
            id="opportunityType"
            value-field="opportunityTypeId"
            text-field="name"
            :class="{ 'is-invalid': $v.opportunityTypeId.$error }"
            @input="updateOpportunityType($event)"
          >
          </b-form-select>
          <span
            class="span-feedback invalid-feedback"
            v-if="$v.opportunityTypeId.$error"
            >You must select a type.</span
          >
        </b-form-group>

        <b-form-group
          label="Category"
          label-for="category"
          label-class="control-label opportunity-form-label required"
          :class="{ 'invalid-group': $v.opportunityCategoryId.$error }"
        >
          <b-form-select
            v-model="opportunityCategoryId"
            :options="validCategoryOptions"
            id="category"
            value-field="opportunityCategoryId"
            text-field="name"
            :class="{ 'is-invalid': $v.opportunityCategoryId.$error }"
            :disabled="!opportunityTypeId"
          ></b-form-select>
          <span
            class="span-feedback invalid-feedback"
            v-if="$v.opportunityCategoryId.$error"
            >You must select a category.</span
          >
        </b-form-group>

        <b-form-group
          label="Site"
          label-for="site"
          label-class="control-label opportunity-form-label required"
          :class="{ 'invalid-group': $v.siteId.$error }"
        >
          <autocomplete
            ref="siteAutocomplete"
            :source="sitesApi"
            v-model="siteId"
            placeholder="Search Sites"
            :inputClass="{
              'form-control': true,
              'is-invalid': $v.siteId.$error,
            }"
            resultsValue="siteId"
            :resultsDisplay="siteDisplay"
            credentials="include"
            @clear="clearSite"
            :request-headers="requestHeaders"
            @selected="setSiteInfo"
          ></autocomplete>
          <span class="span-feedback invalid-feedback" v-if="$v.siteId.$error"
            >You must select a site.</span
          >
        </b-form-group>

        <b-form-group
          label="Semester"
          label-for="semester"
          label-class="control-label opportunity-form-label required"
          :class="{ 'invalid-group': $v.termId.$error }"
        >
          <b-form-select
            v-model="termId"
            :options="terms"
            id="semester"
            value-field="termId"
            text-field="description"
            :class="{ 'is-invalid': $v.termId.$error }"
          ></b-form-select>
          <span class="span-feedback invalid-feedback" v-if="$v.termId.$error"
            >You must select a term.</span
          >
        </b-form-group>

        <b-form-group
          label="Assign Course"
          label-for="course"
          label-class="control-label opportunity-form-label"
          :class="{ 'invalid-group': $v.classRecordId.$error }"
        >
          <autocomplete
            ref="courseAutoComplete"
            :disableInput="!termId"
            :source="courseApi"
            v-model="classRecordId"
            placeholder="Search Courses"
            :inputClass="{
              'form-control': true,
              'is-invalid': $v.classRecordId.$error,
            }"
            resultsValue="classRecordId"
            :resultsDisplay="classDisplay"
            credentials="include"
            @clear="clearCourse"
            :request-headers="requestHeaders"
            @selected="setCourseInfo"
          ></autocomplete>
          <span
            class="span-feedback invalid-feedback"
            v-if="$v.classRecordId.$error"
            >You must select a course.</span
          >
        </b-form-group>

        <b-form-group
          label="Translational Team"
          label-class="control-label opportunity-form-label"
          v-if="isTranslationalTeam"
        >
          <autocomplete
            ref="ttAutocomplete"
            :source="ttApi"
            v-model="translationalTeamId"
            placeholder="Search Translational Teams"
            :inputClass="{
              'form-control': true,
              'is-invalid': $v.translationalTeamId.$error,
            }"
            resultsValue="translationalTeamId"
            resultsDisplay="name"
            credentials="include"
            @clear="clearTranslationalTeam"
            :request-headers="requestHeaders"
          ></autocomplete>
        </b-form-group>

        <b-form-group
          label="Positions Available"
          label-class="control-label opportunity-form-label"
        >
          <b-form-input
            v-model="positionsAvailable"
            type="number"
          ></b-form-input>
        </b-form-group>

        <b-form-group
          label="Commitment Level"
          label-class="control-label opportunity-form-label"
          v-if="isTranslationalTeam"
        >
          <b-form-input v-model="commitmentLevel" type="text"></b-form-input>
        </b-form-group>

        <b-form-group
          label="Minimum GPA"
          label-class="control-label opportunity-form-label"
        >
          <b-form-input
            v-model="recommendedGpa"
            type="number"
            step="0.01"
          ></b-form-input>
        </b-form-group>

        <b-form-group
          label="Recommended Credit Hours"
          label-class="control-label opportunity-form-label"
        >
          <b-form-input
            v-model="recommendedCreditHoursString"
            type="text"
            maxlength="500"
          ></b-form-input>
        </b-form-group>

        <slot name="buttons"></slot>
      </b-form>
    </b-col>
  </b-row>
</template>

<script>
import { required } from "vuelidate/lib/validators";
import * as _ from "lodash"; // eslint-disable-line no-unused-vars

export default {
  data() {
    return {
      requestHeaders: { "X-Requested-With": "XMLHttpRequest" },
      translationalTeamOptions: [],
      classRecordId: null,
    };
  },

  props: {
    categories: {
      type: Array,
      required: true,
    },
    types: {
      type: Array,
      required: true,
    },
    terms: {
      type: Array,
      required: true,
    },
    dirty: {
      type: Boolean,
    },
    opportunity: {
      type: Object,
    },
  },

  validations: {
    opportunityCategoryId: {
      required,
    },
    opportunityTypeId: {
      required,
    },
    siteId: {
      required,
    },
    termId: {
      required,
    },
    translationalTeamId: {
      required: false,
    },
    classRecordId: {
      required: false,
    },
  },

  watch: {
    dirty: function () {
      if (this.dirty) {
        this.$v.$touch();
      }
    },

    "$v.$error": function () {
      var validState = true;
      if (this.$v.$error) {
        validState = false;
      }
      this.$store.commit("updateDetailsValidation", validState);
    },

    opportunity: function () {
      this.name = this.opportunity.name;
      this.opportunityCategoryId = this.opportunity.opportunityCategoryId;
      this.opportunityTypeId = this.opportunity.opportunityTypeId;
      this.siteId = this.opportunity.siteId;
      this.translationalTeamId = this.opportunity.translationalTeamId;
      this.termId = this.opportunity.termId;
      this.positionsAvailable = this.opportunity.positionsAvailable;
      this.commitmentLevel = this.opportunity.commitmentLevel;
      this.recommendedGpa = this.opportunity.recommendedGPA;
      this.recommendedCreditHoursString =
        this.opportunity.recommendedCreditHoursString;
      this.subjectCode = this.opportunity.subjectCode;
      this.catalogNumber = this.opportunity.catalogNumber;
      this.scheduleLineNumber = this.opportunity.scheduleLineNumber;
      this.unitsMinimum = this.opportunity.unitsMinimum;
      this.unitsMaximum = this.opportunity.unitsMaximum;

      this.setSite();

      this.$nextTick(() => {
        if (this.opportunity.translationalTeam) {
          this.$refs.ttAutocomplete.display =
            this.opportunity.translationalTeam.name;
        }
        if (this.opportunity.scheduleLineNumber) {
          this.$refs.courseAutoComplete.display = this.classDisplay(
            this.opportunity
          );
        }
      });
    },

    opportunityTypeId: function () {
      var that = this;
      var opportunityType = _.find(this.types, function (type) {
        return type.opportunityTypeId === that.opportunityTypeId;
      });
      if (opportunityType) {
        this.$store.dispatch("checkTypeValidity", opportunityType);
      }
    },

    // if type is changed to not be translational team, set translationalTeamId to be null
    isTranslationalTeam: function () {
      if (!this.isTranslationalTeam) {
        this.translationalTeamId = null;
      }
    },
  },

  computed: {
    sitesApi: function () {
      return process.env.VUE_APP_BASE_URI + "/api/Sites/FindSite?query=";
    },

    ttApi: function () {
      return (
        process.env.VUE_APP_BASE_URI +
        "/api/TranslationalTeams/GetActiveTranslationalTeams?query="
      );
    },

    courseApi: function () {
      var term = _.find(this.terms, (term) => {
        return term.termId === this.termId;
      });
      if (term) {
        return (
          process.env.VUE_APP_BASE_URI +
          "/api/classes?termcode=" +
          term.termCode +
          "&query="
        );
      }
      return process.env.VUE_APP_BASE_URI + "/api/classes?&query=";
    },

    // categories that actually match the selected opportunity type
    validCategoryOptions: function () {
      var that = this;
      if (this.opportunityTypeId) {
        return _.filter(this.categories, function (category) {
          return _.find(
            category.opportunityTypeOpportunityCategories,
            function (opportunityTypeOpportunityCategory) {
              return (
                opportunityTypeOpportunityCategory.opportunityTypeId ===
                that.opportunityTypeId
              );
            }
          );
        });
      }
      return this.categories;
    },

    // two-way computed properties to keep form updated alongside vuex store
    name: {
      get() {
        return this.$store.state.opportunityForm.opportunity.name;
      },
      set(value) {
        this.$store.commit("updateOpportunityName", value);
      },
    },

    opportunityCategoryId: {
      get() {
        return this.$store.state.opportunityForm.opportunity
          .opportunityCategoryId;
      },
      set(value) {
        this.$store.commit("updateOpportunityCategoryId", value);
      },
    },

    opportunityTypeId: {
      get() {
        return this.$store.state.opportunityForm.opportunity.opportunityTypeId;
      },
      set(value) {
        this.$store.commit("updateOpportunityTypeId", value);
      },
    },

    siteId: {
      get() {
        return this.$store.state.opportunityForm.opportunity.siteId;
      },
      set(value) {
        this.$store.commit("updateSiteId", value);
      },
    },

    termId: {
      get() {
        return this.$store.state.opportunityForm.opportunity.termId;
      },
      set(value) {
        this.$store.commit("updateTermId", value);
      },
    },

    translationalTeamId: {
      get() {
        return this.$store.state.opportunityForm.opportunity
          .translationalTeamId;
      },
      set(value) {
        this.$store.commit("updateTranslationalTeamId", value);
      },
    },

    positionsAvailable: {
      get() {
        return this.$store.state.opportunityForm.opportunity.positionsAvailable;
      },
      set(value) {
        this.$store.commit("updatePositionsAvailable", value);
      },
    },

    commitmentLevel: {
      get() {
        return this.$store.state.opportunityForm.opportunity.commitmentLevel;
      },
      set(value) {
        this.$store.commit("updateCommitmentLevel", value);
      },
    },

    recommendedGpa: {
      get() {
        return this.$store.state.opportunityForm.opportunity.recommendedGpa;
      },
      set(value) {
        this.$store.commit("updateRecommendedGpa", value);
      },
    },

    recommendedCreditHoursString: {
      get() {
        return this.$store.state.opportunityForm.opportunity
          .recommendedCreditHoursString;
      },
      set(value) {
        this.$store.commit("updateRecommendedCreditHoursString", value);
      },
    },

    scheduleLineNumber: {
      get() {
        return this.$store.state.opportunityForm.opportunity.scheduleLineNumber;
      },
      set(value) {
        this.$store.commit("updateScheduleLineNumber", value);
      },
    },

    unitsMinimum: {
      get() {
        return this.$store.state.opportunityForm.opportunity.unitsMinimum;
      },
      set(value) {
        this.$store.commit("updateField", {
          field: "unitsMinimum",
          value: value,
        });
      },
    },

    unitsMaximum: {
      get() {
        return this.$store.state.opportunityForm.opportunity.unitsMinimum;
      },
      set(value) {
        this.$store.commit("updateField", {
          field: "unitsMaximum",
          value: value,
        });
      },
    },

    subjectCode: {
      get() {
        return this.$store.state.opportunityForm.opportunity.subjectCode;
      },
      set(value) {
        this.$store.commit("updateSubjectCode", value);
      },
    },

    catalogNumber: {
      get() {
        return this.$store.state.opportunityForm.opportunity.catalogNumber;
      },
      set(value) {
        this.$store.commit("updateCatalogNumber", value);
      },
    },

    location: {
      get() {
        return this.$store.state.opportunityForm.opportunity.location;
      },
      set(value) {
        this.$store.commit("updateField", { field: "location", value: value });
      },
    },

    locationCode: {
      get() {
        return this.$store.state.opportunityForm.opportunity.locationCode;
      },
      set(value) {
        this.$store.commit("updateField", {
          field: "locationCode",
          value: value,
        });
      },
    },

    sessionCode: {
      get() {
        return this.$store.state.opportunityForm.opportunity.sessionCode;
      },
      set(value) {
        this.$store.commit("updateField", {
          field: "sessionCode",
          value: value,
        });
      },
    },
    sessionLd: {
      get() {
        return this.$store.state.opportunityForm.opportunity.sessionLd;
      },
      set(value) {
        this.$store.commit("updateField", { field: "sessionLd", value: value });
      },
    },
    instructionMode: {
      get() {
        return this.$store.state.opportunityForm.opportunity.instructionMode;
      },
      set(value) {
        this.$store.commit("updateField", {
          field: "instructionMode",
          value: value,
        });
      },
    },

    classStartDate: {
      get() {
        return this.$store.state.opportunityForm.opportunity.classStartDate;
      },
      set(value) {
        this.$store.commit("updateField", {
          field: "classStartDate",
          value: value,
        });
      },
    },

    classEndDate: {
      get() {
        return this.$store.state.opportunityForm.opportunity.classEndDate;
      },
      set(value) {
        this.$store.commit("updateField", {
          field: "classEndDate",
          value: value,
        });
      },
    },

    isTranslationalTeam: function () {
      var that = this;
      if (this.opportunityTypeId) {
        var selectedType = _.find(this.types, function (type) {
          return type.opportunityTypeId === that.opportunityTypeId;
        });
        if (selectedType && selectedType.name === "Translational Team") {
          return true;
        }
      }
      return false;
    },
  },

  methods: {
    siteDisplay: function (site) {
      return site.name + " (" + site.siteId + ")";
    },
    setSite: function () {
      this.$nextTick(() => {
        if (this.opportunity.site) {
          this.$refs.siteAutocomplete.display = this.opportunity.site.name;
        }
      });
    },

    clearSite: function () {
      this.$store.commit("updateSiteId", null);
    },

    clearTranslationalTeam: function () {
      this.$store.commit("updateTranslationalTeamId", null);
    },

    clearCourse: function () {
      this.$store.commit("updateScheduleLineNumber", null);
      this.$store.commit("updateSubjectCode", null);
      this.$store.commit("updateCatalogNumber", null);
      this.$store.commit("updateField", { field: "unitsMinimum", value: null });
      this.$store.commit("updateField", { field: "unitsMaximum", value: null });
      this.$store.commit("updateField", { field: "location", value: null });
      this.$store.commit("updateField", { field: "locationCode", value: null });
      this.$store.commit("updateField", { field: "sessionCode", value: null });
      this.$store.commit("updateField", { field: "sessionLd", value: null });
      this.$store.commit("updateField", {
        field: "instructionMode",
        value: null,
      });
    },

    setSiteInfo: function (object) {
      this.$store.commit(
        "updateSiteContacts",
        object.selectedObject.siteContacts
      );
    },

    setCourseInfo: function (object) {
      this.subjectCode = object.selectedObject.subjectCode;
      this.catalogNumber = object.selectedObject.catalogNumber;
      this.scheduleLineNumber = object.selectedObject.scheduleLineNumber;
      this.unitsMinimum = object.selectedObject.unitsMinimum;
      this.unitsMaximum = object.selectedObject.unitsMaximum;
      this.location = object.selectedObject.location;
      this.locationCode = object.selectedObject.locationCode;
      this.sessionCode = object.selectedObject.sessionCode;
      this.sessionLd = object.selectedObject.sessionLd;
      this.instructionMode = object.selectedObject.instructionMode;
      this.classStartDate = object.selectedObject.startDate;
      this.classEndDate = object.selectedObject.endDate;
      if (
        object.selectedObject.unitsMinimum &&
        object.selectedObject.unitsMaximum &&
        object.selectedObject.unitsMinimum ===
          object.selectedObject.unitsMaximum
      ) {
        this.recommendedCreditHoursString =
          object.selectedObject.unitsMaximum + " credits";
      } else if (
        object.selectedObject.unitsMinimum &&
        object.selectedObject.unitsMaximum &&
        object.selectedObject.unitsMinimum !==
          object.selectedObject.unitsMaximum
      ) {
        this.recommendedCreditHoursString =
          object.selectedObject.unitsMinimum +
          " - " +
          object.selectedObject.unitsMaximum +
          " credits";
      }
    },

    classDisplay: function (record) {
      var text =
        record.subjectCode +
        " " +
        record.catalogNumber +
        " ( SLN: " +
        record.scheduleLineNumber +
        " )";
      if (record.asurite) {
        text +=
          " - " + record.asurite.firstName + " " + record.asurite.lastName;
      }

      return text;
    },

    // $event: opportunityTypeId
    updateOpportunityType: function ($event) {
      var opportunityType = _.find(this.types, function (type) {
        return type.opportunityTypeId == $event;
      });
      this.$store.commit("updateOpportunityType", opportunityType);
    },
  },

  created() {
    this.setSite();
  },
};
</script>

<style scoped>
.opportunity-form-label {
  margin-bottom: 0;
  padding-bottom: 0;
}

.row {
  font-size: 13px;
}

label.required:after {
  content: " *";
  color: #dc3545;
  font-weight: bold;
}
</style>
